/**
 * hooks/useAuthImage.ts
 * ─────────────────────
 * Fetch an image URL with the JWT Bearer header and return a local blob URL.
 * The image endpoint requires auth, so a plain <img src="..."> won't work.
 *
 * Return values
 * ─────────────
 *   null    – fetch in progress (show a loading state)
 *   false   – fetch completed but failed (404, 401, network error, etc.)
 *   string  – blob URL ready to use in <img src>
 *
 * Cache design (sized for 2,000+ location missions)
 * ─────────────────────────────────────────────────
 * • Blob URLs are cached in a module-level LRU bounded by TOTAL BYTES, not
 *   entry count — a full-res detail image (~700 KB) and a list thumbnail
 *   (~20 KB) cost what they actually weigh. On eviction the blob URL is
 *   revoked so the browser frees the memory; the next mount of that image
 *   simply refetches. Entries are re-touched on every mount so on-screen
 *   images stay hot. (Unbounded caching previously held 750 MB+ of blobs on
 *   large missions — the memory pressure made fetches fail mid-scroll.)
 * • Failures are NOT permanent: a failed URL is blacklisted for FAIL_TTL_MS
 *   and then retried. Mounted hooks schedule their own retry when the entry
 *   lapses, so a transient network blip / tab suspend no longer blanks a
 *   thumbnail for the rest of the session.
 */

import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

/** Total bytes of blob data kept alive before LRU eviction kicks in. */
export const MAX_CACHE_BYTES = 100 * 1024 * 1024;
/** How long a failed URL stays blacklisted before it may be retried. */
export const FAIL_TTL_MS = 30_000;

interface CacheEntry {
  url: string;
  bytes: number;
}

// Map iteration order = insertion order, so deleting + re-setting on access
// keeps the least-recently-used entry first.
const _cache = new Map<string, CacheEntry>();
const _failedAt = new Map<string, number>();
const _inFlight = new Map<string, Promise<string | false>>();
let _cacheBytes = 0;

function _touch(src: string, entry: CacheEntry): void {
  _cache.delete(src);
  _cache.set(src, entry);
}

function _insert(src: string, entry: CacheEntry): void {
  const existing = _cache.get(src);
  if (existing) {
    _cacheBytes -= existing.bytes;
    if (existing.url !== entry.url) URL.revokeObjectURL(existing.url);
    _cache.delete(src);
  }
  _cache.set(src, entry);
  _cacheBytes += entry.bytes;
  // Evict oldest-first until under budget, always keeping the newest entry.
  while (_cacheBytes > MAX_CACHE_BYTES && _cache.size > 1) {
    const oldest = _cache.entries().next().value;
    if (!oldest) break;
    const [oldSrc, oldEntry] = oldest;
    _cache.delete(oldSrc);
    _cacheBytes -= oldEntry.bytes;
    URL.revokeObjectURL(oldEntry.url);
  }
}

/** ms until the failure blacklist entry expires; 0 when not blacklisted.
 *  Expired entries are pruned as a side effect. */
function _failRemaining(src: string): number {
  const at = _failedAt.get(src);
  if (at === undefined) return 0;
  const remaining = at + FAIL_TTL_MS - Date.now();
  if (remaining <= 0) {
    _failedAt.delete(src);
    return 0;
  }
  return remaining;
}

function _load(src: string): Promise<string | false> {
  const existing = _inFlight.get(src);
  if (existing) return existing;

  const p: Promise<string | false> = apiClient.getRaw(src)
    .then(async (res) => {
      if (!res.ok) {
        _failedAt.set(src, Date.now());
        return false as const;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      _failedAt.delete(src);
      _insert(src, { url, bytes: blob.size });
      return url;
    })
    .catch(() => {
      _failedAt.set(src, Date.now());
      return false as const;
    })
    .finally(() => {
      _inFlight.delete(src);
    });

  _inFlight.set(src, p);
  return p;
}

export function useAuthImage(src: string | null): string | null | false {
  // Bumped to re-run the effect once a failure blacklist entry expires.
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<string | null | false>(() => {
    if (!src) return null;
    if (_failRemaining(src) > 0) return false;
    return _cache.get(src)?.url ?? null;
  });

  useEffect(() => {
    if (!src) {
      setState(null);
      return;
    }

    // Blacklisted → show the failed state now, retry when the entry expires
    // (jittered so a mass failure doesn't retry in lockstep).
    const failRemaining = _failRemaining(src);
    if (failRemaining > 0) {
      setState(false);
      const timer = setTimeout(
        () => setAttempt((a) => a + 1),
        failRemaining + 100 + Math.random() * 900,
      );
      return () => clearTimeout(timer);
    }

    const cached = _cache.get(src);
    if (cached) {
      _touch(src, cached);
      setState(cached.url);
      return;
    }

    let cancelled = false;
    _load(src).then((result) => {
      if (cancelled) return;
      setState(result);
      // A fresh failure re-enters the blacklist branch above, which
      // schedules the retry timer.
      if (result === false) setAttempt((a) => a + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [src, attempt]);

  return state;
}
